import Image from 'next/future/image';
import cn from 'classnames';
import settings from '@/settings';
import marketplaceImage from '@/public/icons/homepage/marketplace/marketplace.png';
import marketplaceMobileImage from '@/public/icons/homepage/marketplace/marketplace-mobile.png';
import i18n from '@/settings/i18n';
import {
  CONTENT,
  DEFAULT_CONTENT,
  FITNESS_CATEGORIES,
  HOME_APPLIANCES_CATEGORIES,
  // LUXURY_CATEGORIES,
} from '@/lib/usp-helper';
import { CATEGORY_CONTENT } from '@/lib/collections/category/marketplace';

const t = i18n.init();
interface MarketplaceFeatureBoxProps {
  title: string;
  imageSrc: StaticImageData;
  desc: string;
}

const MarketplaceFeatureBox = (props: MarketplaceFeatureBoxProps) => {
  const { title, imageSrc, desc } = props;

  return (
    <div>
      <div className="flex items-center justify-start">
        <div className="relative h-6 w-6 lg:h-7 lg:w-7">
          <Image
            src={imageSrc}
            alt={title}
            className="h-full w-full object-contain"
          />
        </div>
        <b className="ml-2 text-base">{t(title)}</b>
      </div>
      <p className="mt-1.5 text-sm">{desc}</p>
    </div>
  );
};

export default function Marketplace({
  view,
  category = 'default',
  handle = 'default',
}: {
  view: 'default' | 'product' | 'category';
  category?: string;
  handle?: string;
}) {
  const store = ['reebelo-us', 'reebelo-ca'].includes(settings.store)
    ? 'na'
    : 'apac';

  const generateContent = () => {
    if (view === 'product') {
      // Luxury - To be enabled when we introduce categories that fall under Luxury
      // if (LUXURY_CATEGORIES.includes(category))
      //   return CONTENT.product[store].luxury;
      // Fitness
      if (FITNESS_CATEGORIES.includes(category))
        return CONTENT.product[store].fitness;
      // Home Appliances
      if (HOME_APPLIANCES_CATEGORIES.includes(category))
        return CONTENT.product[store].home_appliances;

      // Default
      return CONTENT.product[store].electronics;
    }

    if (view === 'category' && handle !== 'default') {
      const content = CATEGORY_CONTENT[handle as keyof typeof CATEGORY_CONTENT];

      if (content != null)
        return CATEGORY_CONTENT[handle as keyof typeof CATEGORY_CONTENT];
    }

    return DEFAULT_CONTENT[store];
  };

  const content = generateContent();

  return (
    <div className="reebelo-container">
      <h2 className="whitespace-normal text-center text-xl font-semibold xxs:text-left xs:text-2xl md:text-3xl lg:whitespace-pre-wrap">
        {content.title}
      </h2>
      <div
        className={cn('mt-3 flex flex-col items-center xxs:flex-row xs:mt-0', {
          'justify-end': !content.subTitle,
        })}
      >
        <p className="pb-3 text-center text-sm xxs:mr-3 xxs:pb-6 xxs:text-left md:text-base lg:max-w-[70%] lg:pb-0">
          {content.subTitle}
        </p>
        <div className="w-28 shrink-0 place-self-center xxs:mr-5 xxs:place-self-end xs:w-36 sm:mt-0 lg:hidden">
          <Image
            src={marketplaceMobileImage}
            className="h-auto w-full"
            alt="Marketplace"
          />
        </div>
      </div>

      <div className="relative grid grid-cols-1 gap-5 rounded-t-3xl bg-white p-8 md:grid-cols-3 lg:mt-8 lg:grid-cols-4">
        {content.usps.map((c, i) => (
          <MarketplaceFeatureBox
            title={c.title}
            desc={c.desc}
            imageSrc={c.icon}
            key={`${c.title}-${c.desc}-${i}`}
          />
        ))}
        <div className="absolute bottom-0 right-[-15px] hidden lg:block">
          <div
            className={cn({
              'lg:w-[300px] xl:w-[270px]': content.subTitle.length,
              'lg:w-[275px] xl:w-[250px]': !content.subTitle.length,
            })}
          >
            <Image
              src={marketplaceImage}
              className="h-auto w-full"
              alt="Marketplace"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
